import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, limit, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { categoryLabel } from '../../lib/format.js';

type Stage = 'uploaded' | 'extracting' | 'classifying' | 'embedding' | 'ready' | 'triage' | 'error';

const STAGES: Stage[] = ['uploaded', 'extracting', 'classifying', 'embedding', 'ready', 'triage', 'error'];

const STAGE_COLOR: Record<Stage, string> = {
  uploaded: 'rgb(168 162 158)',
  extracting: 'rgb(14 165 233)',
  classifying: 'rgb(245 158 11)',
  embedding: 'rgb(139 92 246)',
  ready: 'rgb(5 150 105)',
  triage: 'rgb(249 115 22)',
  error: '#9C4A3A',
};

function stageLabel(s: Stage): string {
  try {
    return categoryLabel(s);
  } catch {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
}

export function ProcessingStatusWidget({ title = 'Document processing' }: { title?: string }) {
  const [counts, setCounts] = useState<Record<Stage, number> | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'documents'), limit(1000));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next: Record<Stage, number> = {
          uploaded: 0,
          extracting: 0,
          classifying: 0,
          embedding: 0,
          ready: 0,
          triage: 0,
          error: 0,
        };
        snap.docs.forEach((d) => {
          const status = (d.data() as { status?: Stage }).status;
          if (status && status in next) next[status] += 1;
        });
        setCounts(next);
      },
      // WHY: non-admins cannot list documents — render nothing instead of crashing.
      () => setCounts(null),
    );
    return () => unsub();
  }, []);

  const total = useMemo(
    () => (counts ? STAGES.reduce((sum, s) => sum + counts[s], 0) : 0),
    [counts],
  );

  if (!counts || total === 0) return null;

  const ready = counts.ready;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-stone-800">{title}</div>
        <div className="text-[10px] tabular-nums text-stone-500">
          {ready}/{total}
        </div>
      </div>

      <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-stone-100">
        {STAGES.map((s) =>
          counts[s] > 0 ? (
            <div
              key={s}
              style={{ width: `${(counts[s] / total) * 100}%`, backgroundColor: STAGE_COLOR[s] }}
            />
          ) : null,
        )}
      </div>

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {STAGES.filter((s) => counts[s] > 0).map((s) => (
          <span key={s} className="text-[10px] text-stone-600">
            <span style={{ color: STAGE_COLOR[s] }}>●</span> {stageLabel(s)} {counts[s]}
          </span>
        ))}
      </div>

      <div className="mt-2">
        <Link to="/documents/processing" className="text-[10px] text-[color:var(--sfp-primary)]">
          Open processing monitor →
        </Link>
      </div>
    </div>
  );
}
